import * as React from 'react';
import { useFormik } from 'formik';
import { FilterSelectNative } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { NotesCard } from './notes-card';
import { NotesEditor } from './notes-editor';
import { useSession } from '@/hooks/useSession';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { Loader } from '@/components/custom/loader';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNote } from '@/hooks/useNote';
import { NotesAside, NotesBody, NotesNav, NotesView } from './notes';
import { FormField } from '../form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardFooter, CardHeader, CardTitle, CardText, CardWrapper } from '@/components/ui/card';

export function NotesClassroom(props) {
  const { viewClassroom, setViewClassroom, setSearchTerm, classrooms, setClassroomData, notes, searchTerm, activeNote, setActiveNote } = props;

  const { updateNewClassroomNotes, hasMoreNewClassroomNotes } = useNote();

  const [pageNumber, setPageNumber] = React.useState(1);

  const { session } = useSession();
  const { t } = useTranslation();

  const filters = useFormik({
    initialValues: {
      filter: 'blank'
    }
  });

  const options = [
    { name: 'Minhas anotações', id: 1 },
    { name: 'Anotações importantes', id: 2 },
    { name: 'Novas anotações', id: 3 }
  ];

  const filterNotes = notes => {
    let filteredNotes = notes;

    if (searchTerm !== '') {
      filteredNotes = filteredNotes.filter(a => a?.title != null && a?.title.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (filters.values.filter === '1') {
      const currentUser = session.user.name;
      filteredNotes = filteredNotes.filter(note => note.user.name === currentUser);
    } else if (filters.values.filter === '2') {
      filteredNotes = filteredNotes.filter(note => note.bookmarked === true);
    } else if (filters.values.filter === '3') {
      filteredNotes = filteredNotes.filter(note => note.new_note === true);
    }

    return filteredNotes;
  };

  const filteredNotes = filterNotes(notes);

  React.useEffect(() => {
    if (pageNumber !== 1) {
      updateNewClassroomNotes(pageNumber);
    }
  }, [pageNumber]);

  if (!classrooms?.length > 0)
    return (
      <EmptyState
        type="register"
        title={'Você ainda não está enturmado'}
        text={'Para fazer anotações em uma turma, você precisa fazer parte dela. Fale com seu coordenador para enturmar você.'}
        // TODO: implement coordinator contact action
        cta={{ text: 'Falar com o coordenador', onClick: () => {} }}
      />
    );

  if (!viewClassroom) {
    return (
      <InfiniteScroll
        dataLength={classrooms.length}
        next={() => setPageNumber(pageNumber + 1)}
        hasMore={hasMoreNewClassroomNotes}
        loader={<Loader />}
        className="w-full"
      >
        <NotesBody className="sm:flex-col sm:gap-4">
          <CardWrapper className="pt-4">
            {classrooms ? (
              classrooms.map((classroom, index) => (
                <Card
                  key={index}
                  variant="group"
                  className="flex flex-col"
                >
                  <CardHeader className="flex-grow">
                    <CardTitle>{classroom?.institution?.name}</CardTitle>

                    <Tooltip id="institution" />
                    <CardText
                      className="truncate"
                      data-tooltip-id="institution"
                      data-tooltip-content={classroom?.course?.title}
                    >
                      {classroom?.course?.title}
                    </CardText>

                    <Tooltip id="course" />
                    <CardText
                      className="truncate"
                      data-tooltip-id="course"
                      data-tooltip-content={classroom?.title}
                    >
                      {classroom?.title}
                    </CardText>

                    {classroom['has-new-note'] && (
                      <Badge
                        variant="warning"
                        size="xs"
                      >
                        Novas anotações
                      </Badge>
                    )}
                  </CardHeader>
                  <CardFooter>
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      onClick={() => {
                        setViewClassroom(true);
                        setClassroomData(classroom);
                      }}
                    >
                      {t('button.access')}
                    </Button>
                  </CardFooter>
                </Card>
              ))
            ) : (
              <Loader />
            )}
          </CardWrapper>
        </NotesBody>
      </InfiniteScroll>
    );
  }

  return (
    <NotesBody>
      <NotesAside>
        <FormField
          aria-label={'Pesquisar'}
          placeholder={t('textsCommon.research')}
          type="search"
          name="search"
          className="mb-2"
          onChange={e => setSearchTerm(e.target.value)}
        />
        <FilterSelectNative
          blankLabel={'Selecione um filtro'}
          label={null}
          value={filters.values.filter}
          onChange={e => filters.setFieldValue('filter', e.target.value)}
          options={options}
        />
        <NotesNav>
          {filteredNotes.map(note => (
            <NotesCard
              note={note}
              index={note.id}
              key={note.id}
              activeNote={activeNote}
              setActiveNote={setActiveNote}
              user={{ image: note.user.image.url, name: note.user.name, id: note.user.id }}
            />
          ))}
        </NotesNav>
      </NotesAside>

      <NotesView>
        <NotesEditor
          note={activeNote}
          emptyStateTitle={t('emptyState.titleViewClassNotes')}
          emptyStateText={t('emptyState.textViewClassNotes')}
        />
      </NotesView>
    </NotesBody>
  );
}
